import {
  FlexColumn,
  FlexRow,
  LabeledInput,
  MaterialIcon,
  StyledButton,
  useLoading,
} from '@gorila-shared-ui/components';
import { Checkbox } from 'baseui/checkbox';
import { useEffect, useMemo, useState } from 'react';
import { areEqual } from 'react-window';
import { FEEDBACK, FEEDBACK_PREFIXES } from '../../constants/app';
import { EMPTY_SUB_CLIENT } from '../../constants/subClients';
import { useFeedback } from '../../hooks/useFeedback';
import { useStyles } from '../../hooks/useStyles';
import { createSubClient, updateSubClient } from '../../services/subClientService';
import { ClientCore } from '../../types/client';
import { ScalingMatrix, MinimalSubClient, SubClient } from '../../types/subClient';
import { ContactForm } from '../ui/ContactForm';
import { HeadingXSmall, LabelMedium } from 'baseui/typography';
import { ScalingMatrixForm } from './ScalingMatrixForm';
import useUpdateEffect from '../../hooks/useUpdateEffect';

type Props = {
  clientId?: string;
  subClient?: SubClient;
  onCancel: () => void;
  afterSave: () => void;
};
export function SubClientForm({ subClient, clientId, onCancel, afterSave }: Readonly<Props>) {
  const { css, theme } = useStyles();
  const [editingSubClient, setEditingSubClient] = useState<MinimalSubClient>(EMPTY_SUB_CLIENT);
  const [editingScalingMatrix, setEditingScalingMatrix] = useState<{ item: ScalingMatrix; isValid: boolean }[]>([]);
  const { startLoading, stopLoading, loading } = useLoading(false);
  const { showFailFeedback, showPositiveFeedback } = useFeedback();
  const [isValidContact, setIsValidContact] = useState(false);

  useEffect(() => {
    if (subClient) {
      setEditingSubClient(subClient);
    }
    if (clientId) {
      setEditingSubClient((prev) => ({ ...prev, client: { _id: clientId } as ClientCore }));
    }
  }, [subClient, clientId]);

  useUpdateEffect(() => {
    if (editingSubClient.controlTower && subClient) {
      setEditingSubClient((prev) => ({
        ...prev,
        controlTowerScalingMatrix: subClient.controlTowerScalingMatrix || [],
      }));
      setEditingScalingMatrix(
        subClient.controlTowerScalingMatrix?.map((item) => ({
          item,
          isValid: true,
        })) || [],
      );
    } else {
      setEditingSubClient((prev) => ({ ...prev, controlTowerScalingMatrix: [] }));
    }
  }, [editingSubClient.controlTower]);

  const hasUpdates = !areEqual(subClient ?? {}, editingSubClient);

  const isValidScalingMatrix = useMemo(() => {
    if (!editingSubClient.controlTower) return true;
    return editingScalingMatrix.length > 0 && !editingScalingMatrix.some((item) => !item.isValid);
  }, [editingScalingMatrix]);

  const canSubmit = useMemo(() => {
    if (
      !!editingSubClient.name &&
      !!editingSubClient.tradeName &&
      !!editingSubClient.rfc &&
      isValidContact &&
      isValidScalingMatrix
    )
      return true;
    return false;
  }, [editingSubClient, isValidContact, isValidScalingMatrix]);

  const onInputChange = (value: any, field: keyof MinimalSubClient) => {
    setEditingSubClient((prev) => ({ ...prev, [field]: value }));
  };

  const onScalingMatrixChange = (value: ScalingMatrix, index: number) => {
    const newScalingMatrix = [...(editingScalingMatrix ?? [])];
    newScalingMatrix[index] = {
      ...newScalingMatrix[index],
      item: value,
    };
    onInputChange(
      newScalingMatrix.map((scalingMatrix) => scalingMatrix.item),
      'controlTowerScalingMatrix',
    );
    setEditingScalingMatrix(newScalingMatrix);
  };

  const onAddScalingMatrixItem = () => {
    const newScalingMatrix = {
      item: {
        minutes: 0,
        name: '',
        phone: '',
      },
      isValid: false,
    };
    setEditingScalingMatrix((prev) => [...prev, newScalingMatrix]);
    setEditingSubClient((prev) => ({
      ...prev,
      controlTowerScalingMatrix: [...(prev.controlTowerScalingMatrix || []), newScalingMatrix.item],
    }));
  };

  const onRemoveScalingMatrixItem = (index: number) => {
    const newScalingMatrix = [...editingScalingMatrix];
    newScalingMatrix.splice(index, 1);
    setEditingScalingMatrix(newScalingMatrix);
    setEditingSubClient((prev) => ({
      ...prev,
      controlTowerScalingMatrix: [...newScalingMatrix.map((element) => element.item)],
    }));
  };

  const onSave = async () => {
    startLoading();
    const { id, error } = subClient ? await updateSubClient(editingSubClient) : await createSubClient(editingSubClient);
    if (!error && id) {
      afterSave();
      showPositiveFeedback(
        subClient
          ? FEEDBACK.edited(FEEDBACK_PREFIXES.subClient, editingSubClient.name)
          : FEEDBACK.created(FEEDBACK_PREFIXES.subClient),
      );
    } else {
      showFailFeedback(
        error ||
          (subClient
            ? FEEDBACK.failedEdition(FEEDBACK_PREFIXES.subClient, editingSubClient.name)
            : FEEDBACK.failedCreation(FEEDBACK_PREFIXES.subClient)),
      );
    }
    stopLoading();
  };

  return (
    <FlexColumn
      classNames={css({
        flex: 1,
        justifyContent: 'space-between',
        overflow: 'hidden',
      })}
    >
      <FlexColumn
        classNames={css({
          overflowY: 'auto',
          height: '100%',
        })}
      >
        <FlexRow
          classNames={`${css({
            alignItems: 'center',
            justifyContent: 'space-between',
            minHeight: theme.sizing.scale950,
          })}`}
        >
          <HeadingXSmall margin={0}>Generales</HeadingXSmall>
        </FlexRow>
        <LabeledInput
          label="Nombre:"
          value={editingSubClient.name}
          onChange={(value) => onInputChange(value, 'name')}
          required
        />
        <LabeledInput
          label="Nombre comercial:"
          value={editingSubClient.tradeName}
          onChange={(value) => onInputChange(value, 'tradeName')}
          required
        />
        <LabeledInput
          label="RFC:"
          value={editingSubClient.rfc}
          onChange={(value) => onInputChange(value, 'rfc')}
          maxLength={14}
          required
        />
        <Checkbox
          checked={editingSubClient.active}
          onChange={({ currentTarget: { checked } }) => onInputChange(checked, 'active')}
        >
          Activo
        </Checkbox>
        <div
          className={css({
            borderTop: '1px solid lightGray',
            paddingTop: '1em',
          })}
        >
          <ContactForm
            label="Contacto de emergencia"
            contact={editingSubClient.contact}
            onChange={(value) => onInputChange(value, 'contact')}
            isValid={(valid) => setIsValidContact(valid)}
          />
        </div>
        <FlexColumn
          classNames={css({
            flex: 1,
            borderTop: '1px solid lightGray',
            paddingTop: '1em',
            boxSizing: 'border-box',
          })}
        >
          <LabelMedium>Torre de control</LabelMedium>
          <FlexRow classNames={css({ justifyContent: 'space-between' })}>
            <Checkbox
              checked={editingSubClient.controlTower}
              onChange={() => onInputChange(!editingSubClient.controlTower, 'controlTower')}
            >
              Activo
            </Checkbox>
            {editingSubClient.controlTower && (
              <StyledButton
                size="mini"
                kind="tertiary"
                shape="pill"
                onClick={onAddScalingMatrixItem}
              >
                <MaterialIcon name="add" />
                Agregar Contacto
              </StyledButton>
            )}
          </FlexRow>
          {editingSubClient.controlTower && (
            <FlexColumn>
              {editingScalingMatrix?.map((scalingMatrix, i) => {
                return (
                  <div
                    key={i}
                    className={css({
                      borderTop: i !== 0 ? '1px solid lightGray' : undefined,
                      paddingTop: i !== 0 ? '1em' : undefined,
                    })}
                  >
                    <ScalingMatrixForm
                      scalingMatrixItem={scalingMatrix.item}
                      onChange={(scalingMatrixItem) => onScalingMatrixChange(scalingMatrixItem, i)}
                      isValid={(isValid) =>
                        setEditingScalingMatrix((prev) => {
                          const newMatrix = [...prev];
                          newMatrix[i].isValid = isValid;
                          return newMatrix;
                        })
                      }
                      onRemove={() => onRemoveScalingMatrixItem(i)}
                    />
                  </div>
                );
              })}
            </FlexColumn>
          )}
        </FlexColumn>
      </FlexColumn>
      <FlexRow classNames={`${css({ alignItems: 'center', justifyContent: 'flex-end', marginTop: 'auto' })}`}>
        <FlexRow gap={theme.sizing.scale300}>
          {subClient?._id && (
            <StyledButton
              kind="tertiary"
              onClick={onCancel}
            >
              Cancelar
            </StyledButton>
          )}
          <StyledButton
            onClick={onSave}
            isLoading={loading}
            disabled={!canSubmit || !hasUpdates}
          >
            Guardar
          </StyledButton>
        </FlexRow>
      </FlexRow>
    </FlexColumn>
  );
}
